import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppShell } from './shell/AppShell';
import { Toasts } from './shell/Toasts';
import { ThemeSync } from './shell/Theme';
import { meStore } from './shell/me';
import { WorkspacesPage } from './pages/WorkspacesPage';
import { BoardsPage } from './pages/BoardsPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/Placeholders';
import { useInstalledApp, useNoPageZoom } from './hooks/useNoPageZoom';
// Registers the notifications bell (header slot `notifications`); must come after AppShell.
import './features/sharing/shell';
import { AdminPage } from './features/sharing/pages/AdminPage';
import { WorkspaceSettingsPage } from './features/sharing/pages/WorkspaceSettingsPage';
import { InventoryPage } from './features/inventory/InventoryPage';

const EditorPage = lazy(() => import('./editor/EditorPage'));
const GuestPage = lazy(() => import('./features/sharing/guest/GuestPage'));
const InvitePage = lazy(() =>
  import('./features/appointments/InvitePage').then((m) => ({ default: () => <m.InvitePage /> })),
);

/** Share links and invitation answers are opened without an account: never bounce to the login. */
export function isGuestPath(path: string): boolean {
  return path.startsWith('/s/') || path.startsWith('/invite/');
}

function Loading() {
  const { t } = useTranslation();
  return <div className="page muted">{t('common.loading')}</div>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/b/:boardId"
        element={
          <Suspense fallback={<Loading />}>
            <EditorPage />
          </Suspense>
        }
      />
      <Route
        path="/s/:token"
        element={
          <Suspense fallback={<Loading />}>
            <GuestPage />
          </Suspense>
        }
      />
      <Route
        path="/invite/:token"
        element={
          <Suspense fallback={<Loading />}>
            <InvitePage />
          </Suspense>
        }
      />
      <Route element={<AppShell />}>
        <Route index element={<WorkspacesPage />} />
        <Route path="/w/:wsId" element={<BoardsPage />} />
        <Route path="/w/:wsId/settings" element={<WorkspaceSettingsPage />} />
        <Route path="/w/:wsId/inventory" element={<InventoryPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function App() {
  useNoPageZoom(useInstalledApp());
  useEffect(() => {
    const guest = typeof window !== 'undefined' && isGuestPath(window.location.pathname);
    void meStore.getState().load(guest ? { redirectOn401: false } : {});
  }, []);
  return (
    <BrowserRouter>
      <ThemeSync />
      <AppRoutes />
      <Toasts />
    </BrowserRouter>
  );
}
